import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StyledDialog from "../mui/StyledDialog";
import { useI18n } from "../../hooks/useI18n";
import { theme } from "../../layout/Theme";
import { EvcScanner } from "./EvcScanner";
import { type Evc } from "@syadem/evc-js";
import { parseEvcToken } from "./parseEvcToken";
import { useDaphne } from "../../hooks/useDaphne";
import { getTranslatedFieldByLocale } from "../../../utils/translatedFields";
import { getDiseasesNames } from "../../hooks/useVaccinesWithDiseases";
import { VaccinationAct } from "@syadem/kairos-citizen-js";
import { dayjs } from "../../../utils/dayjs";
import { useKairosApi } from "../../hooks/useKairosApi";
import { CreateVaccinationActBody } from "@syadem/kairos-citizen-js";

interface EvcImportDialogProps {
  open: boolean;
  onClose: () => void;
  healthRecordVaccinations?: VaccinationAct[];
  healthRecordId?: string;
  healthRecordName?: string;
  healthRecordBirthDate?: string;
  onImportComplete?: () => void;
}

interface VaccinationMatch {
  evcVaccinationId: number;
  nuvaCode: number;
  vaccineName: string;
  diseasesLabel: string;
  countryCode: string;
  performedOn: string;
  status: "exact" | "new";
}

interface EvcCreateOperation {
  id: string;
  evcVaccinationId: number;
  nuvaCode: number;
  label: string;
  performedOn: string;
  countryCode: string;
  diseasesLabel: string;
  request?: CreateVaccinationActBody;
  status: "pending" | "loading" | "success" | "error";
  error?: string;
}

export function EvcImportDialog({
  open,
  onClose,
  healthRecordVaccinations,
  healthRecordId,
  healthRecordName,
  healthRecordBirthDate,
  onImportComplete,
}: EvcImportDialogProps) {
  const { t, locale } = useI18n();
  const daphne = useDaphne();
  const api = useKairosApi();
  const [activeStep, setActiveStep] = useState(0);
  const [token, setToken] = useState<string | null>(null);
  const [selectedToAdd, setSelectedToAdd] = useState<Record<number, boolean>>({});
  const [parsedEvc, setParsedEvc] = useState<Evc | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [operationResults, setOperationResults] = useState<EvcCreateOperation[]>([]);

  const steps = useMemo(
    () => [t("vaccines.scan_evc_step_scan"), t("vaccines.scan_evc_step_preview"), t("vaccines.scan_evc_step_import")],
    [t],
  );

  const evcData = useMemo(() => {
    if (!token) return null;
    if (!parsedEvc) return null;
    return {
      dateOfBirth: parsedEvc.dateOfBirth.toISOString().slice(0, 10),
      name: parsedEvc.name,
      issuer: parsedEvc.issuer,
      issuedAt: parsedEvc.issuedAt?.toISOString(),
      expiresAt: parsedEvc.expiresAt?.toISOString(),
      vaccinations:
        parsedEvc.vaccinations?.map((vaccination) => {
          const vaccine = daphne.repositories.vaccines
            .all()
            .find((candidate) => candidate.code === vaccination.nuvaCode);
          const vaccineName = vaccine
            ? getTranslatedFieldByLocale(locale, vaccine.name)
            : `NUVA ${vaccination.nuvaCode}`;
          const diseases = vaccine ? daphne.queries.diseasesByVaccine(vaccine) : [];
          const diseasesLabel = getDiseasesNames(diseases, locale || "en");

          return {
            id: vaccination.id,
            nuvaCode: vaccination.nuvaCode,
            vaccineName,
            diseasesLabel: diseasesLabel || "-",
            countryCode: vaccination.countryCode,
            performedOn: vaccination.performedOn.toISOString().slice(0, 10),
            booster: vaccination.booster,
          };
        }) || [],
    };
  }, [token, parsedEvc, daphne, locale]);
  const healthRecordVaccinationsSource = useMemo(() => {
    if (!daphne) {
      return [];
    }
    if (!healthRecordVaccinations || healthRecordVaccinations.length === 0) {
      return [];
    }
    return healthRecordVaccinations
      .map((act) => {
        const vaccine = daphne.repositories.vaccines.find(act.vaccineId);
        if (!vaccine || !act.performedOn) return null;
        return {
          ehrId: act.id,
          nuvaCode: vaccine.code,
          performedOn: dayjs.utc(act.performedOn).format("YYYY-MM-DD"),
        };
      })
      .filter((value): value is { ehrId: string; nuvaCode: number; performedOn: string } => value !== null);
  }, [healthRecordVaccinations, daphne]);
  const matches = useMemo<VaccinationMatch[]>(() => {
    if (!evcData?.vaccinations) return [];
    const ehrIndex = new Set(healthRecordVaccinationsSource.map((ehr) => `${ehr.nuvaCode}|${ehr.performedOn}`));
    return evcData.vaccinations.map((vaccination) => {
      const key = `${vaccination.nuvaCode}|${vaccination.performedOn}`;
      const status: VaccinationMatch["status"] = ehrIndex.has(key) ? "exact" : "new";
      return {
        evcVaccinationId: vaccination.id,
        nuvaCode: vaccination.nuvaCode,
        vaccineName: vaccination.vaccineName || `NUVA ${vaccination.nuvaCode}`,
        diseasesLabel: vaccination.diseasesLabel || "-",
        countryCode: vaccination.countryCode,
        performedOn: vaccination.performedOn,
        status,
      };
    });
  }, [evcData, healthRecordVaccinationsSource]);

  const matchStats = useMemo(() => {
    const exact = matches.filter((match) => match.status === "exact").length;
    const fresh = matches.filter((match) => match.status === "new").length;
    return { exact, fresh, total: matches.length };
  }, [matches]);

  const selectedCount = useMemo(
    () => matches.filter((match) => match.status === "new" && selectedToAdd[match.evcVaccinationId]).length,
    [matches, selectedToAdd],
  );

  const plannedOperations = useMemo<EvcCreateOperation[]>(() => {
    return matches
      .filter((match) => match.status === "new" && selectedToAdd[match.evcVaccinationId])
      .map((match) => {
        const vaccine = daphne.repositories.vaccines.all().find((candidate) => candidate.code === match.nuvaCode);
        if (!vaccine) {
          return {
            id: `evc-${match.evcVaccinationId}`,
            evcVaccinationId: match.evcVaccinationId,
            nuvaCode: match.nuvaCode,
            label: match.vaccineName,
            performedOn: match.performedOn,
            countryCode: match.countryCode,
            diseasesLabel: match.diseasesLabel,
            status: "error",
            error: t("vaccines.scan_evc_missing_vaccine", { nuvaCode: match.nuvaCode }),
          };
        }

        if (!healthRecordId) {
          return {
            id: `evc-${match.evcVaccinationId}`,
            evcVaccinationId: match.evcVaccinationId,
            nuvaCode: match.nuvaCode,
            label: match.vaccineName,
            performedOn: match.performedOn,
            countryCode: match.countryCode,
            diseasesLabel: match.diseasesLabel,
            status: "error",
            error: t("vaccines.scan_evc_missing_health_record"),
          };
        }

        const performedOn = dayjs.utc(match.performedOn).toDate();
        const request: CreateVaccinationActBody = {
          vaccinationAct: {
            performedOn,
            vaccineId: vaccine.id,
            countryCode: match.countryCode || undefined,
          },
        };

        return {
          id: `evc-${match.evcVaccinationId}`,
          evcVaccinationId: match.evcVaccinationId,
          nuvaCode: match.nuvaCode,
          label: match.vaccineName,
          performedOn: match.performedOn,
          countryCode: match.countryCode,
          diseasesLabel: match.diseasesLabel,
          request,
          status: "pending",
        };
      });
  }, [matches, selectedToAdd, daphne, healthRecordId, t]);

  const displayedOperations = operationResults.length > 0 ? operationResults : plannedOperations;
  const successCount = useMemo(
    () => displayedOperations.filter((operation) => operation.status === "success").length,
    [displayedOperations],
  );
  const allSucceeded = useMemo(
    () => displayedOperations.length > 0 && displayedOperations.every((operation) => operation.status === "success"),
    [displayedOperations],
  );

  useEffect(() => {
    if (!token) return;
    const nextSelection: Record<number, boolean> = {};
    for (const match of matches) {
      if (match.status === "new") {
        nextSelection[match.evcVaccinationId] = true;
      }
    }
    setSelectedToAdd(nextSelection);
  }, [token, matches]);

  useEffect(() => {
    if (!token) {
      setParsedEvc(null);
      setParseError(null);
      setIsParsing(false);
      return;
    }
    let isCancelled = false;
    const run = async () => {
      setIsParsing(true);
      setParseError(null);
      try {
        const evc = await parseEvcToken(token);
        if (!isCancelled) {
          setParsedEvc(evc);
        }
      } catch (error) {
        if (!isCancelled) {
          setParseError(error instanceof Error ? error.message : t("vaccines.scan_evc_parse_error"));
          setParsedEvc(null);
        }
      } finally {
        if (!isCancelled) {
          setIsParsing(false);
        }
      }
    };
    run();
    return () => {
      isCancelled = true;
    };
  }, [token, t]);
  const vaccinationsCount = evcData?.vaccinations.length ?? null;
  const evcDateOfBirth = evcData?.dateOfBirth ?? null;
  const fullName = evcData?.name ? `${evcData.name.firstName} ${evcData.name.lastName}` : null;

  const healthRecordDob = useMemo(() => {
    if (!healthRecordBirthDate) return null;
    return dayjs.utc(healthRecordBirthDate).format("YYYY-MM-DD");
  }, [healthRecordBirthDate]);

  const nameMatches = useMemo(() => {
    if (!healthRecordName || !fullName) return null;
    const normalize = (value: string) =>
      value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .replace(/\s+/g, " ");
    return normalize(fullName) === normalize(healthRecordName);
  }, [healthRecordName, fullName]);

  const birthDateMatches = useMemo(() => {
    if (!evcDateOfBirth || !healthRecordDob) return null;
    return evcDateOfBirth === healthRecordDob;
  }, [evcDateOfBirth, healthRecordDob]);

  const showIdentityWarning = useMemo(
    () => nameMatches === false || birthDateMatches === false,
    [nameMatches, birthDateMatches],
  );

  const resetFlow = () => {
    setToken(null);
    setActiveStep(0);
    setSelectedToAdd({});
    setParsedEvc(null);
    setParseError(null);
    setIsParsing(false);
    setIsSubmitting(false);
    setSubmitError(null);
    setOperationResults([]);
  };

  const handleSubmit = async () => {
    if (!api || !healthRecordId) {
      setSubmitError(t("vaccines.scan_evc_missing_health_record"));
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    setOperationResults(
      plannedOperations.map((operation) => ({
        ...operation,
        status: operation.status === "error" ? "error" : "loading",
      })),
    );

    let successCount = 0;
    for (const operation of plannedOperations) {
      setOperationResults((prev) =>
        prev.map((item) =>
          item.id === operation.id && item.status !== "error" ? { ...item, status: "loading" } : item,
        ),
      );
      if (!operation.request) {
        setOperationResults((prev) =>
          prev.map((item) =>
            item.id === operation.id
              ? { ...item, status: "error", error: operation.error || t("vaccines.scan_evc_submit_error") }
              : item,
          ),
        );
        continue;
      }
      try {
        await api.vaccinationActApi.createVaccinationAct(healthRecordId, operation.request);
        successCount += 1;
        setOperationResults((prev) =>
          prev.map((item) => (item.id === operation.id ? { ...item, status: "success" } : item)),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : t("vaccines.scan_evc_submit_error");
        setOperationResults((prev) =>
          prev.map((item) => (item.id === operation.id ? { ...item, status: "error", error: message } : item)),
        );
      }
    }

    if (successCount > 0) {
      onImportComplete?.();
    }

    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetFlow();
    onClose();
  };

  const canGoNext = activeStep === 0 ? !!token : activeStep === 1 ? selectedCount > 0 : true;
  const isLastStep = activeStep === steps.length - 1;

  const hasSubmission = operationResults.length > 0;
  const dialogActions = (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        justifyContent: "flex-end",
      }}
    >
      {isSubmitting ? (
        <Button variant="contained" disabled startIcon={<CircularProgress size={18} />}>
          {t("vaccines.scan_evc_submitting")}
        </Button>
      ) : (
        <>
          {activeStep > 0 && !hasSubmission && (
            <Button variant="outlined" onClick={() => setActiveStep((prev) => prev - 1)}>
              {t("common.cta.back")}
            </Button>
          )}
          {isLastStep ? (
            hasSubmission ? (
              <Button variant={allSucceeded ? "contained" : "outlined"} onClick={handleClose}>
                {t("vaccines.scan_evc_done")}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleSubmit} disabled={selectedCount === 0}>
                {t("vaccines.scan_evc_add_selected")}
              </Button>
            )
          ) : (
            <Button variant="contained" onClick={() => setActiveStep((prev) => prev + 1)} disabled={!canGoNext}>
              {t("common.cta.next")}
            </Button>
          )}
        </>
      )}
    </Box>
  );

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      title={t("vaccines.scan_evc_dialog_title")}
      dialogActions={dialogActions}
      hideCancelAction={isSubmitting}
      maxWidth="md"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <EvcScanner
              onToken={(value) => {
                setToken(value);
                setActiveStep(1);
              }}
              active={activeStep === 0 && open}
            />
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle1">{t("vaccines.scan_evc_preview_title")}</Typography>
            {isParsing && (
              <Typography variant="body2" color={theme.palette.neutral[600]}>
                {t("vaccines.scan_evc_parsing")}
              </Typography>
            )}
            {parseError && (
              <Typography variant="body2" color={theme.palette.error.main}>
                {parseError}
              </Typography>
            )}
            <Box
              sx={{
                border: `1px solid ${theme.palette.neutral[200]}`,
                borderRadius: 2,
                padding: 2,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box>
                  <Typography variant="caption" color={theme.palette.neutral[600]}>
                    {t("vaccines.scan_evc_preview_name")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">{fullName || "-"}</Typography>
                    {nameMatches !== null &&
                      (nameMatches ? (
                        <CheckCircleOutlineIcon fontSize="small" color="success" />
                      ) : (
                        <Tooltip
                          title={t("vaccines.scan_evc_name_mismatch_tooltip", {
                            name: healthRecordName || "-",
                          })}
                        >
                          <span>
                            <WarningAmberIcon fontSize="small" color="warning" />
                          </span>
                        </Tooltip>
                      ))}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color={theme.palette.neutral[600]}>
                    {t("vaccines.scan_evc_preview_dob")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">{evcDateOfBirth || "-"}</Typography>
                    {birthDateMatches !== null &&
                      (birthDateMatches ? (
                        <CheckCircleOutlineIcon fontSize="small" color="success" />
                      ) : (
                        <Tooltip
                          title={t("vaccines.scan_evc_birthdate_mismatch_tooltip", {
                            birthdate: healthRecordDob || "-",
                          })}
                        >
                          <span>
                            <WarningAmberIcon fontSize="small" color="warning" />
                          </span>
                        </Tooltip>
                      ))}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box>
                  <Typography variant="caption" color={theme.palette.neutral[600]}>
                    {t("vaccines.scan_evc_preview_vaccinations")}
                  </Typography>
                  <Typography variant="body2">{vaccinationsCount !== null ? vaccinationsCount : "-"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color={theme.palette.neutral[600]}>
                    {t("vaccines.scan_evc_preview_issuer")}
                  </Typography>
                  <Typography variant="body2">{evcData?.issuer || "-"}</Typography>
                </Box>
              </Box>
              {showIdentityWarning && (
                <Alert severity="warning" sx={{ gridColumn: "1 / -1" }}>
                  {t("vaccines.scan_evc_name_mismatch_warning")}
                </Alert>
              )}
            </Box>
            <Divider />
            <Typography variant="body2" color={theme.palette.neutral[600]}>
              {t("vaccines.scan_evc_preview_summary", {
                total: matchStats.total,
                exact: matchStats.exact,
                new: matchStats.fresh,
                selected: selectedCount,
              })}
            </Typography>
            {matchStats.total > 0 && matchStats.fresh === 0 && (
              <Alert severity="success">{t("vaccines.scan_evc_all_present")}</Alert>
            )}
            <List sx={{ border: `1px solid ${theme.palette.neutral[200]}`, borderRadius: 2 }}>
              {matches.map((match, index) => (
                <ListItem key={match.evcVaccinationId} divider={index < matches.length - 1}>
                  <ListItemText
                    primary={`${match.vaccineName}`}
                    secondary={`${t("vaccines.scan_evc_preview_date", { date: match.performedOn })} • ${t(
                      "vaccines.scan_evc_preview_diseases",
                      {
                        diseases: match.diseasesLabel || "-",
                      },
                    )}`}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {match.status === "new" && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedToAdd[match.evcVaccinationId]}
                            onChange={() =>
                              setSelectedToAdd((prev) => ({
                                ...prev,
                                [match.evcVaccinationId]: !prev[match.evcVaccinationId],
                              }))
                            }
                          />
                        }
                        label={t("vaccines.scan_evc_select_to_add")}
                      />
                    )}
                    <Chip
                      label={t(`vaccines.scan_evc_status_${match.status}`)}
                      color={match.status === "exact" ? "success" : "default"}
                      variant={match.status === "new" ? "outlined" : "filled"}
                    />
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {submitError && (
              <Typography variant="body2" color={theme.palette.error.main}>
                {submitError}
              </Typography>
            )}
            <Typography variant="body2" color={theme.palette.neutral[600]}>
              {allSucceeded
                ? t("vaccines.scan_evc_import_summary_success", { count: successCount })
                : t("vaccines.scan_evc_import_summary", { selected: selectedCount })}
            </Typography>
            <List sx={{ border: `1px solid ${theme.palette.neutral[200]}`, borderRadius: 2 }}>
              {displayedOperations.map((operation, index) => (
                <ListItem key={operation.id} divider={index < displayedOperations.length - 1}>
                  <ListItemText
                    primary={`${operation.label} • NUVA ${operation.nuvaCode}`}
                    secondary={`${t("vaccines.scan_evc_preview_date", { date: operation.performedOn })} • ${t(
                      "vaccines.scan_evc_preview_diseases",
                      {
                        diseases: operation.diseasesLabel || "-",
                      },
                    )}${operation.error ? ` • ${t("vaccines.scan_evc_error_label")}: ${operation.error}` : ""}`}
                  />
                  <Chip
                    icon={operation.status === "loading" ? <CircularProgress size={14} /> : undefined}
                    label={t(`vaccines.scan_evc_operation_${operation.status}`)}
                    color={
                      operation.status === "success" ? "success" : operation.status === "error" ? "error" : "default"
                    }
                    variant={operation.status === "pending" || operation.status === "loading" ? "outlined" : "filled"}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </StyledDialog>
  );
}
